import { useContext } from "react";

import {
  FRAUDULENT_PAYMENT_INTENT,
  ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE,
  PaymentMethod,
  sleep,
} from "@simplyk/common";
import { useStripe } from "@stripe/react-stripe-js";
import { StripeError } from "@stripe/stripe-js";
import { UseFormSetValue } from "react-hook-form";

import { PaymentErrorReport, unknownPaymentErrorCode } from "./helper";
import { HandlePostSubmit } from "./useStripePayment";

import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { getBillingDetails } from "@/common/helpers/payment";
import { PaymentMode } from "@/common/helpers/stripe";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { SubmitCommandFormInput } from "@/components/PaymentProcessor/helper";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useSubmitCommand } from "@/components/PaymentProcessor/hooks/useSubmitCommand";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { isPaymentFailed } from "@/components/PaymentProcessor/type";

export const usePad = ({
  setValue,
  handlePaymentError,
  handlePostSubmit,
}: {
  setValue: UseFormSetValue<DonationFormPaymentInput | TicketingPaymentInput>;
  handlePaymentError: (report: PaymentErrorReport) => void;
  handlePostSubmit: HandlePostSubmit;
}) => {
  const { handleSubmitCommand } = useSubmitCommand({ setValue });
  const { t } = useTranslate();
  const { organization, commandId, formType, setIsSubmitting } = useContext(FrontendFormContext);
  const stripe = useStripe();

  const payByPad = async (
    submitCommandInput: SubmitCommandFormInput,
    paymentMode: PaymentMode,
    minimumDuration: number
  ): Promise<string | undefined> => {
    const express = Boolean(submitCommandInput.isExpressCheckout);

    if (!stripe) {
      handlePaymentError({
        rawErrorCode: "stripe_load_error",
        rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.pad2.code }),
        step: "stripe-load",
        paymentMethod: PaymentMethod.Pad,
        isExpressCheckout: express,
        commandId,
        isKnownError: false,
      });
      return;
    }

    const submitResponse = await handleSubmitCommand({ ...submitCommandInput, paymentMethod: PaymentMethod.Pad });

    if (submitResponse && isPaymentFailed(submitResponse)) {
      handlePaymentError({
        rawErrorCode: submitResponse.error.code || "server_submit_error",
        rawErrorMessage:
          submitResponse.error.code === ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE
            ? ORGANIZATION_CANNOT_ACCEPT_PAYMENT_WITHOUT_BANK_ERROR_CODE
            : submitResponse.error.message,
        step: "server-submit",
        paymentMethod: PaymentMethod.Pad,
        isExpressCheckout: express,
        commandId,
        isKnownError: true,
      });
      return;
    }

    const clientSecret = submitResponse.data?.clientSecret;
    const command = submitResponse.data?.command;
    const transaction = submitResponse.data?.transaction ?? null;
    const emailUpdateToken = submitResponse.data?.emailUpdateToken;
    const buyerTicketToken = submitResponse.data?.buyerTicketToken;
    const { paymentInput, isExpressCheckout = false } = submitCommandInput;

    if (clientSecret === FRAUDULENT_PAYMENT_INTENT.clientSecret) {
      const result = await handlePostSubmit({
        command,
        transaction,
        formType,
        paymentInput,
        isExpressCheckout,
        emailUpdateToken,
        buyerTicketToken,
      });
      if (result.error || !result.returnUrl) {
        handlePaymentError({
          rawErrorCode: result.error ? "post_submit_error" : "missing_return_url",
          rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.pad1.code }),
          step: "post-submit",
          paymentMethod: PaymentMethod.Pad,
          isExpressCheckout: express,
          commandId: command?.id,
          isKnownError: false,
        });
        return;
      }
      return result.returnUrl;
    }

    if (!clientSecret || !command) {
      handlePaymentError({
        rawErrorCode: !clientSecret ? "missing_client_secret" : "missing_command",
        rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.pad2.code }),
        step: "server-submit",
        paymentMethod: PaymentMethod.Pad,
        isExpressCheckout: express,
        commandId,
        isKnownError: false,
      });
      return;
    }

    let confirmError: StripeError | undefined;
    if (paymentMode === "setup") {
      const { error } = await stripe.confirmAcssDebitSetup(clientSecret, {
        payment_method: {
          billing_details: getBillingDetails(submitCommandInput.paymentInput, organization),
        },
        mandate_data: {
          acss_debit: {
            mandate_options: {
              payment_schedule: "interval",
              interval_description: "on completion of checkout",
              transaction_type: "personal",
            },
          },
        },
      });
      confirmError = error;
    } else {
      const { error } = await stripe.confirmAcssDebitPayment(clientSecret, {
        payment_method: {
          billing_details: getBillingDetails(submitCommandInput.paymentInput, organization),
        },
      });
      confirmError = error;
    }

    if (confirmError) {
      handlePaymentError({
        rawErrorCode: confirmError.code || "pad_confirm_error",
        rawErrorMessage:
          confirmError.message || t("common", "unknown_error", { code: unknownPaymentErrorCode.pad3.code }),
        step: "manual-confirmation",
        paymentMethod: PaymentMethod.Pad,
        isExpressCheckout: express,
        commandId,
        isKnownError: true,
      });
      return;
    }

    const result = await handlePostSubmit({
      paymentInput,
      command,
      transaction,
      formType,
      isExpressCheckout,
      emailUpdateToken,
      buyerTicketToken,
    });

    if (result?.returnUrl) {
      setIsSubmitting(true);
      await sleep(minimumDuration);
      return result.returnUrl;
    }

    if (result?.error) {
      handlePaymentError({
        rawErrorCode: "post_submit_error",
        rawErrorMessage: result.error,
        step: "post-submit",
        paymentMethod: PaymentMethod.Pad,
        isExpressCheckout: express,
        commandId: command.id,
        isPaymentSucceeded: true,
        isKnownError: false,
      });
    } else {
      handlePaymentError({
        rawErrorCode: "missing_return_url",
        rawErrorMessage: t("common", "unknown_error.paymentSucceeded", { code: unknownPaymentErrorCode.pad4.code }),
        step: "post-submit",
        paymentMethod: PaymentMethod.Pad,
        isExpressCheckout: express,
        commandId: command.id,
        isPaymentSucceeded: true,
        isKnownError: false,
      });
    }
  };

  return { payByPad };
};
